import { useEffect, useState } from "react";
import { fetchSettings, adminUpdateSettings } from "@/lib/api";
import { NAV } from "@/lib/nav";
import { keyForPath, useSettings } from "@/context/SettingsContext";
import { Eye, EyeOff, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const GROUPS = NAV.map((g) => ({ label: g.label.en, links: g.items }));

// Sections whose web address can be customised (mirrors backend SECTION_KEYS).
const RENAMABLE = new Set([
  "kathas", "mantras", "suktas", "stotras", "puja-vidhi", "vedic-knowledge", "festivals",
  "scriptures", "rishis", "philosophy", "traditions", "pilgrimage", "samskaras", "sanskrit",
  "explained", "vrata", "deities", "temples", "panchang", "quiz", "journal", "glossary",
]);

const slugify = (s) => (s || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export default function AdminVisibility() {
  const [sections, setSections] = useState({});
  const [labels, setLabels] = useState({});
  const [slugs, setSlugs] = useState({});
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { reload: reloadSettings } = useSettings();

  useEffect(() => {
    fetchSettings().then((d) => {
      setSections(d.sections || {});
      setLabels(d.labels || {});
      setSlugs(d.route_slugs || {});
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const isOn = (key) => sections[key] !== false;
  const flip = (key) => setSections((s) => ({ ...s, [key]: !isOn(key) }));
  const setName = (key, field, val) => setLabels((l) => ({ ...l, [key]: { ...(l[key] || {}), [field]: val } }));
  const resetName = (key) => setLabels((l) => { const n = { ...l }; delete n[key]; return n; });
  const setSlug = (key, val) => setSlugs((s) => ({ ...s, [key]: val }));
  const resetSlug = (key) => setSlugs((s) => { const n = { ...s }; delete n[key]; return n; });

  const save = async () => {
    setBusy(true);
    try {
      const clean = {};
      Object.entries(labels).forEach(([k, v]) => {
        const en = (v?.en || "").trim(); const hi = (v?.hi || "").trim();
        if (en || hi) clean[k] = { ...(en ? { en } : {}), ...(hi ? { hi } : {}) };
      });
      const cleanSlugs = {};
      Object.entries(slugs).forEach(([k, v]) => {
        const s = slugify(v);
        if (s && s !== k && RENAMABLE.has(k)) cleanSlugs[k] = s;
      });
      const res = await adminUpdateSettings({ sections, labels: clean, route_slugs: cleanSlugs });
      setSections(res.sections || {}); setLabels(res.labels || {}); setSlugs(res.route_slugs || {});
      reloadSettings();
      toast("Sections saved", { description: "Names, addresses & visibility updated. Old links redirect automatically." });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error("Save failed", { description: detail });
    } finally { setBusy(false); }
  };

  if (!loaded) return <p className="py-16 text-center text-ink-3">…</p>;

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-serif-latin text-3xl text-ink">Sections — Names, Address & Visibility</h1>
          <p className="mt-1 text-sm text-ink-3">Rename any section, give it a custom web address (e.g. <code>/hymns</code>), and hide/show it. Leave a name or address blank to keep the default. Old addresses redirect to the new one automatically.</p>
        </div>
        <button data-testid="visibility-save" onClick={save} disabled={busy} className="inline-flex shrink-0 items-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
          <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="space-y-8">
        {GROUPS.map((g) => (
          <div key={g.label}>
            <p className="overline-label mb-3 text-ink-3">{g.label}</p>
            <div className="divide-y divide-line border border-line bg-surface">
              {g.links.map((l) => {
                const key = keyForPath(l.to);
                const on = isOn(key);
                const lv = labels[key] || {};
                const customized = !!(lv.en || lv.hi);
                const renamable = RENAMABLE.has(key);
                const slugVal = slugs[key] || "";
                const hasSlug = !!slugify(slugVal) && slugify(slugVal) !== key;
                return (
                  <div key={l.to} data-testid={`section-row-${key}`} className="flex flex-col gap-3 p-4 lg:flex-row lg:items-center">
                    <div className="flex w-40 shrink-0 items-baseline gap-2">
                      <span className="font-deva text-saffron/70">{l.deva}</span>
                      <span className="text-xs text-ink-3">{l.en}</span>
                    </div>
                    <div className="grid flex-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      <input
                        data-testid={`section-name-en-${key}`}
                        value={lv.en || ""}
                        onChange={(e) => setName(key, "en", e.target.value)}
                        placeholder={`English name (default: ${l.en})`}
                        className="border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none"
                      />
                      <input
                        data-testid={`section-name-hi-${key}`}
                        value={lv.hi || ""}
                        onChange={(e) => setName(key, "hi", e.target.value)}
                        placeholder={`देवनागरी (default: ${l.deva})`}
                        className="border border-line bg-parchment px-3 py-2 font-deva-body text-sm text-ink focus:border-saffron focus:outline-none"
                      />
                      {renamable ? (
                        <div className="flex items-center gap-1.5">
                          <div className="flex flex-1 items-center border border-line bg-parchment focus-within:border-saffron">
                            <span className="pl-3 text-sm text-ink-3">/</span>
                            <input
                              data-testid={`section-slug-${key}`}
                              value={slugVal}
                              onChange={(e) => setSlug(key, e.target.value)}
                              placeholder={key}
                              className="w-full bg-transparent px-1 py-2 text-sm text-ink focus:outline-none"
                            />
                          </div>
                          {hasSlug && (
                            <button title="Reset address to default" data-testid={`section-slug-reset-${key}`} onClick={() => resetSlug(key)} className="p-2 text-ink-3 hover:text-saffron"><RotateCcw className="h-4 w-4" /></button>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center px-3 text-xs text-ink-3">{l.to}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {customized && (
                        <button title="Reset name to default" onClick={() => resetName(key)} className="p-2 text-ink-3 hover:text-saffron"><RotateCcw className="h-4 w-4" /></button>
                      )}
                      <button
                        data-testid={`visibility-toggle-${key}`}
                        onClick={() => flip(key)}
                        className={`inline-flex w-28 items-center justify-center gap-1.5 border px-3 py-2 text-xs transition-colors ${on ? "border-line text-saffron" : "border-line bg-surface-alt text-ink-3"}`}
                      >
                        {on ? <><Eye className="h-4 w-4" /> Visible</> : <><EyeOff className="h-4 w-4" /> Hidden</>}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
